/** Gráficos simples em SVG (sem dependências), com a paleta do tema. */
import { useState } from "react";

export interface Series { key: string; label: string; color: string; kind?: "bar" | "line" }

const MES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
export const monthLabel = (m: string) => `${MES[Number(m.slice(5, 7)) - 1]}/${m.slice(2, 4)}`;

function fmtShort(v: number, money?: boolean): string {
  const a = Math.abs(v);
  const s = a >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : a >= 1e4 ? `${(v / 1e3).toFixed(0)}k` : a >= 1e3 ? `${(v / 1e3).toFixed(1)}k` : `${Math.round(v * 10) / 10}`;
  return money ? `R$ ${s}` : s;
}

/**
 * Barras agrupadas por categoria + linhas opcionais (eixo à direita, ex.: %).
 * Passe o mouse para ver os valores.
 */
export function BarChart({ data, x, series, height = 220, money, labelX = (v) => v }: {
  data: Array<Record<string, unknown>>; x: string; series: Series[]; height?: number; money?: boolean; labelX?: (v: string) => string;
}): JSX.Element {
  const [hover, setHover] = useState<number | null>(null);
  const bars = series.filter((s) => s.kind !== "line");
  const lines = series.filter((s) => s.kind === "line");
  const W = 720, H = height, L = 48, R = lines.length ? 40 : 12, T = 12, B = 34;
  const vals = data.flatMap((d) => bars.map((s) => Number(d[s.key] ?? 0)));
  const max = Math.max(1, ...vals), min = Math.min(0, ...vals);
  const lmax = Math.max(1, ...data.flatMap((d) => lines.map((s) => Number(d[s.key] ?? 0))));
  const iw = W - L - R, ih = H - T - B;
  const y = (v: number) => T + ih - ((v - min) / (max - min)) * ih;
  const yl = (v: number) => T + ih - (v / lmax) * ih;
  const band = iw / Math.max(1, data.length);
  const bw = Math.min(28, (band * 0.75) / Math.max(1, bars.length));
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => min + (max - min) * t);
  return (
    <div className="chart">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} onMouseLeave={() => setHover(null)}>
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={L} x2={W - R} y1={y(t)} y2={y(t)} stroke="var(--border)" strokeDasharray={t === 0 ? undefined : "3 3"} />
            <text x={L - 6} y={y(t) + 4} fontSize="10" textAnchor="end" fill="var(--muted)">{fmtShort(t, money)}</text>
          </g>
        ))}
        {data.map((d, i) => {
          const cx = L + band * i + band / 2;
          return (
            <g key={i} onMouseEnter={() => setHover(i)}>
              <rect x={L + band * i} y={T} width={band} height={ih} fill={hover === i ? "var(--surface)" : "transparent"} />
              {bars.map((s, j) => {
                const v = Number(d[s.key] ?? 0);
                const x0 = cx - (bars.length * bw) / 2 + j * bw;
                return <rect key={s.key} x={x0} y={Math.min(y(v), y(0))} width={bw - 2} height={Math.max(1, Math.abs(y(v) - y(0)))} rx="3" fill={s.color} />;
              })}
              <text x={cx} y={H - 12} fontSize="10.5" textAnchor="middle" fill="var(--muted)">{labelX(String(d[x]))}</text>
            </g>
          );
        })}
        {lines.map((s) => {
          const pts = data.map((d, i) => (d[s.key] === null || d[s.key] === undefined ? null : [L + band * i + band / 2, yl(Number(d[s.key]))] as const)).filter(Boolean) as Array<readonly [number, number]>;
          return (
            <g key={s.key}>
              <polyline points={pts.map((p) => p.join(",")).join(" ")} fill="none" stroke={s.color} strokeWidth="2" />
              {pts.map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r="3" fill={s.color} />)}
            </g>
          );
        })}
        {lines.length > 0 && <text x={W - 4} y={T + 8} fontSize="10" textAnchor="end" fill="var(--muted)">{fmtShort(lmax)}</text>}
      </svg>
      <div className="chart-legend">
        {series.map((s) => <span key={s.key}><i style={{ background: s.color }} />{s.label}</span>)}
        {hover !== null && data[hover] && (
          <span className="chart-tip">
            <b>{labelX(String(data[hover]![x]))}:</b>{" "}
            {series.map((s) => `${s.label} ${data[hover]![s.key] === null ? "—" : s.kind === "line" ? data[hover]![s.key] : fmtShort(Number(data[hover]![s.key] ?? 0), money)}`).join(" · ")}
          </span>
        )}
      </div>
    </div>
  );
}

/** Lista com barras horizontais (ranking/distribuição). */
export function HBars({ rows, format = (v) => String(v), color = "var(--primary)", max: maxIn }: {
  rows: Array<{ name: string; value: number; hint?: string; color?: string }>; format?: (v: number) => string; color?: string; max?: number;
}): JSX.Element {
  const max = maxIn ?? Math.max(1, ...rows.map((r) => r.value));
  if (!rows.length) return <div className="muted small">Sem dados no período.</div>;
  return (
    <div className="hbars">
      {rows.map((r, i) => (
        <div key={i} className="hbar">
          <div className="hbar-label" title={r.name}>{r.name}</div>
          <div className="hbar-track"><div className="hbar-fill" style={{ width: `${Math.max(1, (r.value / max) * 100)}%`, background: r.color ?? color }} /></div>
          <div className="hbar-val">{format(r.value)}{r.hint ? <span className="muted small"> {r.hint}</span> : null}</div>
        </div>
      ))}
    </div>
  );
}

/** Barra de progresso de meta com marcador do tempo decorrido. */
export function GoalBar({ pct, elapsedPct, onTrack }: { pct: number | null; elapsedPct: number; onTrack: boolean | null }): JSX.Element {
  const p = Math.min(100, Math.max(0, pct ?? 0));
  return (
    <div className="goalbar" title={`${pct ?? 0}% da meta · ${elapsedPct}% do período decorrido`}>
      <div className="goalbar-fill" style={{ width: `${p}%`, background: onTrack === false ? "var(--amber, #d99a00)" : "var(--green, #2e8b57)" }} />
      <div className="goalbar-mark" style={{ left: `${Math.min(100, elapsedPct)}%` }} />
    </div>
  );
}
